import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  FooterMainContent,
  Information,
  InternationalMemberships,
  PaymentOptions,
  PopularProducts,
  Social,
} from "./FooterComponents";
import { useTranslations } from "hooks/useTranslations";
import { USPslider, Logo } from "@thekilroygroup/shared-ui";

export const MobileFooter = () => {
  const {
    translations: {
      domainInfo,
      usp,
      newFooter: {
        title,
        description,
        ctaTitle,
        ctaLink,
        additionalInformationTitle,
        popularProductsTitle,
        paymentMethodsTitle,
        internationalMembershipsAssociationsTitle,
        IATAnumber,
        socialMediaTitle,
        cvrNumber,
      },
    },
  } = useTranslations();
  const { palette } = useTheme();
  const domainTrans = domainInfo.footer;
  return (
    <Box sx={{ pb: 3.5 }}>
      <USPslider
        usps={usp}
        backgroundColor={palette.primary.ultraLight}
        textColor={palette.grey[800]}
        iconColor={palette.grey[800]}
        paginationColor={palette.grey[800]}
        pagination
      />
      <Container
        sx={{
          pt: 7.5,
        }}
      >
        <FooterMainContent
          title={title}
          description={description}
          cta={ctaTitle}
          ctaUrl={ctaLink}
        />
        <Box sx={sxStyles.links}>
          <Information information={domainTrans.additionalInformation}>
            <Typography
              variant="titleMedium"
              weight="bold"
              sx={sxStyles.listTitle}
            >
              {additionalInformationTitle}
            </Typography>
          </Information>
          <PopularProducts popularProducts={domainTrans.popularProducts}>
            <Typography
              variant="titleMedium"
              weight="bold"
              sx={sxStyles.listTitle}
            >
              {popularProductsTitle}
            </Typography>
          </PopularProducts>
        </Box>
        <Accordion sx={sxStyles.accordion}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon color="primary" />}
            sx={sxStyles.accordionSummary}
          >
            <Typography variant="h5" color={"primary.main"}>
              {paymentMethodsTitle}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={sxStyles.accordionDetails}>
            <PaymentOptions paymentOptions={domainTrans.paymentOptions} />
          </AccordionDetails>
        </Accordion>
        <Accordion sx={sxStyles.accordion}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon color="primary" />}
            sx={sxStyles.accordionSummary}
          >
            <Typography variant="h5" color={"primary.main"}>
              {internationalMembershipsAssociationsTitle}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={sxStyles.accordionDetails}>
            <InternationalMemberships
              memberships={domainTrans.internationalMembershipsAssociations}
            />
            <br />
            <Typography variant="titleMedium">
              {IATAnumber}: {domainTrans.IATAnumber}
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion sx={sxStyles.accordion}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon color="primary" />}
            sx={sxStyles.accordionSummary}
          >
            <Typography variant="h5" color={"primary.main"}>
              {socialMediaTitle}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={sxStyles.accordionDetails}>
            <Social social={domainTrans.socialMedia} />
          </AccordionDetails>
        </Accordion>
        <Stack sx={sxStyles.bottomContainer} alignItems={"center"}>
          <Logo height={25} color={"primary.main"} />

          <Typography variant="titleMedium">
            {domainTrans.address} <br />
            {cvrNumber}: {domainTrans.cvrNumber}
          </Typography>
          <Typography variant="titleMedium">
            {domainTrans.copyrights}
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};

const sxStyles: { [key: string]: SxProps<Theme> } = {
  links: {
    display: "grid",
    columnCount: 2,
    gridTemplateColumns: "1fr 1fr",
    columnGap: 2,
    mt: 6,
    mb: 9,
  },
  listTitle: {
    color: "primary.main",
    mt: 3,
    mb: 2,
  },
  accordion: {
    boxShadow: "none",
    borderTop: `1px solid`,
    borderTopColor: "grey.400",
    "&.Mui-expanded": {
      marginTop: 0,
    },
  },
  accordionSummary: (theme: Theme) => ({
    minHeight: theme.spacing(9.75),
  }),
  accordionDetails: {
    mt: -2,
    pt: 2,
  },
  bottomContainer: {
    borderTop: "1px solid",
    borderTopColor: "grey.400",
    pt: 12,
    pb: 10.5,
    gap: 1.75,
    textAlign: "center",
  },
};
